import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Switch, CdnIcon } from '@shared/ui';
import { useTheme } from '../../../../theme';
import { useToggleable } from '../../../../hooks';
import SureModal from './SureModal/SureModal';
import Tooltip from '../../../../BaseComponents/Tooltip';
import { InfoDetails } from '../../../../Icons';
import { TABLET_MIDDLE_WIDTH } from '../../../../utils';
import { useMediaQuery } from 'react-responsive';

interface Props {
	className?: string;
	description?: string | React.ReactNode;
	isActive: boolean;
	hasSeatsByPassenger?: boolean;
	showInfo?: boolean;
	toggleExtraSeat: (active: boolean) => void;
}

const DoubleSeat: React.FC<Props> = ({
	className,
	description,
	isActive,
	hasSeatsByPassenger,
	showInfo = true,
	toggleExtraSeat
}) => {
	const { DoubleSeat: css } = useTheme('SeatMap');
	const { t } = useTranslation('SeatMap');

	const { isOpen, open, close } = useToggleable(false);
	const { isOpen: isOpenInfo, open: openInfo, close: closeInfo } = useToggleable(false);

	const isTablet = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });

	const toggle = () => {
		if (isOpenInfo) {
			closeInfo();
		}

		if (hasSeatsByPassenger) {
			open();
		} else {
			toggleExtraSeat(!isActive);
		}
	};

	const onConfirm = () => {
		close();
		toggleExtraSeat(!isActive);
	};

	const onClickSwitch = () => {
		if (!isActive && isTablet && showInfo) {
			openInfo();
		} else {
			toggle();
		}
	};

	return (
		<>
			<div className={cn(className, css.container)}>
				<div className={css.switch__wrapper}>
					<Switch
						classes={{ root: css.switch__root }}
						checked={isActive}
						onChange={onClickSwitch}
						label={
							<div className={css.switch__label}>
								<CdnIcon icon="Services/Double seat" />
								{t('Double seat')}
							</div>
						}
					/>
				</div>
				{description && (
					<div className={css.description}>
						{description}{' '}
						<Tooltip
							title={t(
								'When you select this service, it will be automatically applied to all flights of your route'
							)}
						>
							<span className={css.switch__infoIcon}>{InfoDetails}</span>
						</Tooltip>
					</div>
				)}
			</div>

			<SureModal
				open={isOpen}
				title={t('Selected seats will be deleted')}
				description={t(
					!isActive
						? 'When choosing the «Double Seat» service, the seats previously added by the passenger will be deleted'
						: 'When the «Double Seat» service is disabled, the seats added by the passenger earlier will be deleted'
				)}
				onConfirm={onConfirm}
				onClose={close}
				hideCancel={true}
			/>

			<SureModal
				open={isOpenInfo}
				title={t('The service applies to all flights')}
				description={t(
					'When you select this service, it will be automatically applied to all flights of your route'
				)}
				onConfirm={toggle}
				onClose={closeInfo}
				hideCancel={true}
			/>
		</>
	);
};

export default DoubleSeat;
